import React, { useEffect } from 'react';
import { Button, message, Form, Icon, Input } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import modal from '~components/modal';
import { MUTATION_INVOICE_NUMBER } from '~pages/dashboard/reconciliation/booking-details/invoice-editor/queries';

let id = 0;

function InvoiceEditor({ opportunityDetail, onClose, onConfirm, form }) {
  const { t } = useTranslation();
  const { getFieldDecorator, getFieldValue } = form;

  const invoiceNumberArray = opportunityDetail?.completedXbooking?.commission?.invoiceNumber
    ? opportunityDetail?.completedXbooking?.commission?.invoiceNumber.split(',')
    : [];

  useEffect(() => {
    id = invoiceNumberArray.length;
  }, []);

  getFieldDecorator('invoicesKey', {
    initialValue: invoiceNumberArray.length ? invoiceNumberArray.map((_id, index) => index) : [id],
  });

  const [updateCommission, { loading: updateCommissionLoading }] = useMutation(
    MUTATION_INVOICE_NUMBER,
    {
      onError: e => {
        message.error(e.message || t('cms.reconciliation.booking.invoice_editor.failed'));
      },
    },
  );

  const handleSubmit = async () => {
    form.validateFieldsAndScroll(async err => {
      if (err) {
        message.warn(t('cms.reconciliation.booking.invoice_editor.submit.error'));
        return;
      }
      const formData = form.getFieldsValue();

      const invoiceNumbersValid = formData.invoicesKey.map(k => {
        return formData.invoices[k];
      });

      const requestPayload = {
        invoiceNumber: invoiceNumbersValid.join(','),
      };

      const response = await updateCommission({
        variables: {
          input: {
            id: opportunityDetail?.completedXbooking?.commission?.id,
            data: requestPayload,
          },
        },
      });

      if (response?.data?.updateCommission?.result) {
        message.success(t('cms.reconciliation.booking.invoice_editor.success'));
        onConfirm();
        onClose();
      }
    });
  };

  const addInvoice = () => {
    const invoicesKey = getFieldValue('invoicesKey');
    const newInvoicesKey = [...invoicesKey, id++];

    form.setFieldsValue({
      invoicesKey: newInvoicesKey,
    });
  };

  const removeInvoice = k => {
    const invoicesKey = form.getFieldValue('invoicesKey');

    if (invoicesKey.length === 1) {
      return;
    }

    form.setFieldsValue({
      invoicesKey: invoicesKey.filter(key => key !== k),
    });
  };

  const invoicesKey = getFieldValue('invoicesKey');

  const formItems = invoicesKey.map(invoice => (
    <Form.Item required={false} key={invoice}>
      {getFieldDecorator(`invoices[${invoice}]`, {
        validateTrigger: ['onChange', 'onBlur'],
        initialValue: invoiceNumberArray[invoice] || '',
        rules: [
          {
            required: true,
            message: t('cms.reconciliation.booking.invoice_editor.input.error'),
          },
        ],
      })(<Input placeholder={t('cms.reconciliation.booking.invoice_editor.input.placeholder')} />)}
      {invoicesKey.length > 1 ? (
        <Icon
          className="dynamic-delete-button"
          type="minus-circle-o"
          onClick={() => removeInvoice(invoice)}
        />
      ) : null}
    </Form.Item>
  ));

  return (
    <div className="partner-register-city-editor invoice-editor">
      <div className="partner-register-city-editor__header invoice-editor__header">
        {t(`cms.reconciliation.booking.invoice_editor.title`)}
        <CloseOutlined className="partner-register-city-editor__close-button" onClick={onClose} />
      </div>
      <div className="partner-register-city-editor__body">
        <section className="partner-reassign-editor__section partner-register-city-editor__main">
          <main className="partner-reassign-editor__main invoice-editor__form">
            <div className={classNames('partner-reassign-editor__form-item')}>
              <h3 className="partner-reassign-editor__selector-title">
                {t('cms.reconciliation.booking.invoice_editor.edit.label')}:
              </h3>
              {formItems}
              <span className="invoice-editor__link" onClick={addInvoice}>
                {t('cms.reconciliation.booking.invoice_editor.add')}
              </span>
            </div>
          </main>
        </section>
      </div>
      <div className="partner-register-city-editor__footer">
        <Button
          size="large"
          className="partner-register-city-editor__cancel-button"
          onClick={onClose}
        >
          {t('cms.referral.partner.detail.editor.btn.cancel')}
        </Button>
        <Button
          type="primary"
          size="large"
          className="partner-register-city-editor__save-button"
          loading={updateCommissionLoading}
          onClick={handleSubmit}
        >
          {t('cms.referral.partner.detail.editor.btn.save')}
        </Button>
      </div>
    </div>
  );
}

InvoiceEditor.propTypes = {
  onClose: PropTypes.func.isRequired,
};

InvoiceEditor.defaultProps = {
  onClose: () => {},
};

export default Form.create()(
  modal(
    {
      className: 'partner-register-city-editor-modal',
    },
    true,
  )(InvoiceEditor),
);
